import React, { useState, useEffect, useRef } from "react";
import { X, Search, Moon, Sun, MapPin, ChevronDown } from "lucide-react";
import VideoLoader from "../../components/VideoLoader";
import { useAuth } from "../../context/AuthContext";
import api from "../../services";
import {
  getPlaceAutocomplete,
  getPlaceDetailsById,
  getHotelInformation,
} from "../../services/apis";
import { useOrder } from "../../context/OrderContext";
import { useDelivery } from "../../context/DeliveryContext";
import {
  getStoreStatusText,
  generateTimeSlotsForDay,
  getDateInTimezone,
  convertToScheduledDateISO,
  getTimezoneAbbreviation,
  isRestaurantCurrentlyOpen,
  getAsapScheduledTime,
  type Timeline,
  type TimezoneInfo,
  type StoreStatus,
} from "../../lib/utils";
import toast from "react-hot-toast";
type OrderType = "pickup" | "delivery";
interface Taxes {
  category_code?: string;
  tax_percent?: number;
  id?: string;
}
export type Location = {
  id: string;
  hotelName?: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  country: string;
  state: string;
  asapDelay?: number;
  city: string;
  cashEnabled?: boolean;
  phoneNo?: string;
  pickupTimeline?: Timeline[];
  deliveryTimeline?: Timeline[];
  storeTimeline?: Timeline[];
  defaultTip?: boolean;
  distance?: string;
  timezone?: string;
  _timezoneInfo?: TimezoneInfo;
  _storeStatus?: StoreStatus;
  isSearchedAddress?: boolean; // Flag to identify searched addresses
  FlatBlock?: string; // Added to store apt/suite
  deliveryInstructions?: string; // Added to store delivery instructions
  deliveryTipToPartner?: boolean;
  baseTipPercent?: number;
  tipGapPercent?: number;
  withinRadius?: number;
  minOrderAmount?: number;
  deliveryAmount?: number;
  taxPercentage?: Taxes[];
  integrations?: {
    square?: {
      configured?: boolean;
      credentials?: {
        SQUARE_APPLICATION_ID?: string;
        SQUARE_LOCATION_ID?: string;
        [key: string]: string | undefined;
      };
    };
    [key: string]: any;
  };
};

export interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderType: OrderType;
  setOrderType: React.Dispatch<React.SetStateAction<OrderType>>;
  selectedLocation: string | null;
  setSelectedLocation: React.Dispatch<React.SetStateAction<string | null>>;
  onScheduleClick: () => void;
  onLocationObjectSelect?: (location: Location | null) => void;
  setPickupLocation: (location: string) => void;
  setDeliveryAddress: (address: string) => void;
  setSelectedAddress: React.Dispatch<React.SetStateAction<boolean>>;
  onLocationConfirmed?: () => void;
}

interface Address {
  _id?: string;
  type: "Home" | "Office" | "Others";
  tag?: string;
  FlatBlock?: string;
  Area?: string;
  latitude?: number;
  longitude?: number;
  deliveryInstructions?: string;
}

type PlaceSearchResult = {
  id: string;
  placeId: string;
  address: string;
  lat?: number;
  lng?: number;
};

const AUTOCOMPLETE_DEBOUNCE_MS = 350;
const AUTOCOMPLETE_MIN_CHARS = 3;
const buildFullDeliveryAddress = (location: Location): string => {
  const flatBlock = location.FlatBlock?.trim();
  return flatBlock ? `${flatBlock}, ${location.address}` : location.address;
};

// Distance is reported by the backend as a "X mi" style string; parse the
// leading number, falling back to a straight-line miles calculation.
const parseDistanceMiles = (distance?: string): number | null => {
  if (!distance) return null;
  const match = distance.match(/(\d+(\.\d+)?)/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const calculateStraightLineMiles = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusMiles * c;
};

const getTodayOpeningText = (
  location: Location,
  orderType: "pickup" | "delivery",
): string => {
  if (!location) return "Closed";

  const timeline: Timeline[] | undefined =
    orderType === "pickup"
      ? location.pickupTimeline
      : location.deliveryTimeline;

  const timezone =
    location.timezone || location._timezoneInfo?.id || "America/Denver";

  return getStoreStatusText(timeline, timezone);
};

export function AddressModals({
  isOpen,
  onClose,
  orderType,
  setOrderType,
  selectedLocation,
  setSelectedLocation,
  onLocationObjectSelect,
  onScheduleClick,
  setDeliveryAddress,
  setPickupLocation,
  setSelectedAddress,
  onLocationConfirmed,
}: AddressModalProps) {
  const [tempLocation, setTempLocation] = useState<Location | null>(null);
  const {
    pickup,
    delivery,
    selectedAddressTag,
    setSelectedAddressTag,
    deliveryAddress,
    deliveryInstructions,
    setDeliveryInstructions,
    setPickup,
    setDelivery,
    updateTaxesForRestaurant,
  } = useDelivery();
  const [pickupLocations, setPickupLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [fetchingRestaurant, setFetchingRestaurant] = useState(false);
  const [deliverable, setDeliverable] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Location | null>(
    null,
  );
  const [searchText, setSearchText] = useState("");
  const [geoResults, setGeoResults] = useState<PlaceSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSessionTokenRef = useRef<string>("");
  const lastAutocompleteQueryRef = useRef<string>("");
  const autocompleteRequestIdRef = useRef(0);
  const [aptSuite, setAptSuite] = useState("");
  const [deliveryError, setDeliveryError] = useState(false);
  const [pickupOnlyNotice, setPickupOnlyNotice] = useState(false);

  // New states for searched address
  const [showAreaInput, setShowAreaInput] = useState(false);
  const [areaInput, setAreaInput] = useState("");
  const [savingAddress, setSavingAddress] = useState(false);
  const [selectedAddressType, setSelectedAddressType] = useState<
    "Home" | "Office" | "Apartment" | "Hotel" | "Others"
  >("Home");
  const [updatingAddress, setUpdatingAddress] = useState(false);

  const { user, isAuth, setSquareApplicationId, setSquareLocationId } = useAuth();
  const { orderData, setOrderData } = useOrder();

  // Check if selected restaurant is currently open for ASAP orders
  const isAsapAvailable =
    orderType === "pickup" && tempLocation
      ? isRestaurantCurrentlyOpen(tempLocation, "pickup")
      : false;

  const [initialAddressData, setInitialAddressData] = useState<{
    FlatBlock?: string;
    deliveryInstructions?: string;
  } | null>(null);

  const generateSessionToken = () => {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  const ensureSearchSessionToken = () => {
    if (!searchSessionTokenRef.current) {
      searchSessionTokenRef.current = generateSessionToken();
    }
    return searchSessionTokenRef.current;
  };

  const clearSearchSessionToken = () => {
    searchSessionTokenRef.current = "";
    lastAutocompleteQueryRef.current = "";
  };

  const mapContextLocationToModalLocation = (
    location: NonNullable<typeof delivery.locationObject>,
    fallbackName = "Location",
  ): Location => ({
    id: location.id,
    hotelName: location.hotelName,
    name: location.name || fallbackName,
    address: location.address,
    latitude: location.latitude,
    longitude: location.longitude,
    country: location.country || "",
    state: location.state || "",
    city: location.city || "",
    phoneNo: location.phoneNo,
    pickupTimeline: location.pickupTimeline,
    deliveryTimeline: location.deliveryTimeline,
    storeTimeline: location.storeTimeline,
    defaultTip: location.defaultTip,
    distance: location.distance,
    timezone: location.timezone,
    _timezoneInfo: location._timezoneInfo,
    _storeStatus: location._storeStatus,
    isSearchedAddress: location.isSearchedAddress,
    FlatBlock: location.FlatBlock,
    deliveryInstructions: location.deliveryInstructions,
    deliveryTipToPartner: location.deliveryTipToPartner,
    baseTipPercent: location.baseTipPercent,
    tipGapPercent: location.tipGapPercent,
    withinRadius: location.withinRadius,
    minOrderAmount: location.minOrderAmount,
    deliveryAmount: location.deliveryAmount,
  });

  // Pre-tax cart subtotal, used to check the self-delivery minimum order budget
  const cartSubtotal = React.useMemo(() => {
    if (!orderData?.items) return 0;
    return orderData.items.reduce(
      (sum: number, item: any) => sum + item.price * item.quantity,
      0,
    );
  }, [orderData?.items]);

  useEffect(() => {
    if (!isOpen) return;

    setDeliveryError(false);
    setShowAreaInput(false);
    setAreaInput("");
    setSearchText("");
    setGeoResults([]);

    if (orderType === "delivery") {
      if (delivery.locationObject) {
        setTempLocation(
          mapContextLocationToModalLocation(
            delivery.locationObject,
            selectedAddressTag || "Delivery Address",
          ),
        );
        setAptSuite(delivery.locationObject.FlatBlock || "");
      } else {
        setTempLocation(null);
        setAptSuite("");
      }

      if (delivery.restaurantLocation) {
        setSelectedRestaurant(
          mapContextLocationToModalLocation(delivery.restaurantLocation),
        );
      } else {
        setSelectedRestaurant(null);
      }
      return;
    }

    if (pickup.locationObject) {
      setTempLocation(mapContextLocationToModalLocation(pickup.locationObject));
    } else {
      setTempLocation(null);
    }
    setSelectedRestaurant(null);
    setAptSuite("");
  }, [
    isOpen,
    orderType,
    pickup.locationObject,
    delivery.locationObject,
    delivery.restaurantLocation,
    selectedAddressTag,
  ]);

  useEffect(() => {
    if (isOpen) return;
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    clearSearchSessionToken();
    setSearching(false);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      clearSearchSessionToken();
    };
  }, []);

  useEffect(() => {
    if (!isOpen || orderType !== "delivery") return;
    if (!user?.id || !isAuth) return;

    const fetchUserData = async () => {
      setLoadingLocations(true);
      try {
        const res = await api.get(`${process.env.REACT_APP_API_URL}/address`);
        setAddresses(res.data.addresses);
      } catch (error) {
        console.error("Error fetching address", error);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchUserData();
  }, [isOpen, orderType, user?.id, isAuth]);

  const searchAddress = async (query: string, sessionToken?: string) => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < AUTOCOMPLETE_MIN_CHARS) {
      setGeoResults([]);
      setSearching(false);
      lastAutocompleteQueryRef.current = "";
      return;
    }

    const normalizedCacheKey = normalizedQuery.toLowerCase();
    if (normalizedCacheKey === lastAutocompleteQueryRef.current) {
      return;
    }
    lastAutocompleteQueryRef.current = normalizedCacheKey;

    const requestId = ++autocompleteRequestIdRef.current;

    try {
      setSearching(true);
      const res = await getPlaceAutocomplete(
        normalizedQuery,
        undefined,
        undefined,
        sessionToken,
      );
      const data = res.data;
      const rawResults = Array.isArray(data)
        ? data
        : data?.predictions || data?.results || data?.data || [];

      const normalized: PlaceSearchResult[] = rawResults
        .map((item: any) => {
          const placeId = item?.place_id || item?.placeId || item?.id || "";
          const address =
            item?.formatted_address || item?.description || item?.address || "";
          const lat =
            typeof item?.geometry?.location?.lat === "number"
              ? item.geometry.location.lat
              : typeof item?.lat === "number"
                ? item.lat
                : undefined;
          const lng =
            typeof item?.geometry?.location?.lng === "number"
              ? item.geometry.location.lng
              : typeof item?.lng === "number"
                ? item.lng
                : undefined;

          return {
            id: placeId || address,
            placeId: placeId || "",
            address,
            lat,
            lng,
          };
        })
        .filter((item: PlaceSearchResult) => Boolean(item.id && item.address));

      if (requestId === autocompleteRequestIdRef.current) {
        setGeoResults(normalized);
      }
    } catch (err) {
      console.error("Address search error", err);
      if (requestId === autocompleteRequestIdRef.current) {
        setGeoResults([]);
      }
    } finally {
      if (requestId === autocompleteRequestIdRef.current) {
        setSearching(false);
      }
    }
  };

  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    const normalizedValue = value.trim();

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (normalizedValue.length < AUTOCOMPLETE_MIN_CHARS) {
      setGeoResults([]);
      setSearching(false);
      lastAutocompleteQueryRef.current = "";
      return;
    }

    const sessionToken = ensureSearchSessionToken();
    searchDebounceRef.current = setTimeout(() => {
      void searchAddress(normalizedValue, sessionToken);
    }, AUTOCOMPLETE_DEBOUNCE_MS);
  };

  const handleGeoSelect = async (item: PlaceSearchResult) => {
    let lat = item.lat;
    let lng = item.lng;
    let address = item.address;
    const sessionToken = ensureSearchSessionToken();

    if ((typeof lat !== "number" || typeof lng !== "number") && item.placeId) {
      try {
        const detailsRes = await getPlaceDetailsById(
          item.placeId,
          sessionToken,
        );
        const details = detailsRes.data;
        const detailsLat =
          typeof details?.lat === "number"
            ? details.lat
            : typeof details?.location?.lat === "number"
              ? details.location.lat
              : undefined;
        const detailsLng =
          typeof details?.lng === "number"
            ? details.lng
            : typeof details?.location?.lng === "number"
              ? details.location.lng
              : undefined;
        const detailsAddress =
          details?.formattedAddress || details?.address || address;

        lat = typeof detailsLat === "number" ? detailsLat : lat;
        lng = typeof detailsLng === "number" ? detailsLng : lng;
        address = detailsAddress;
      } catch (err) {
        console.error("Error fetching place details by id", err);
      }
    }

    clearSearchSessionToken();

    if (typeof lat !== "number" || typeof lng !== "number") return;

    const location: Location = {
      id: item.placeId || item.id,
      name: "Delivery Address",
      address,
      latitude: lat,
      longitude: lng,
      country: "",
      state: "",
      city: "",
      isSearchedAddress: true, // Mark as searched address
    };

    setSearchText("");
    setGeoResults([]);
    setShowAreaInput(true); // Show area input for searched addresses
    setTempLocation(location);
  };

  const handleSearchFocus = () => {
    ensureSearchSessionToken();
  };

  const handleSearchBlur = () => {
    setTimeout(() => {
      clearSearchSessionToken();
    }, 150);
  };

  const handleSaveSearchedAddress = async () => {
    if (!tempLocation || !tempLocation.isSearchedAddress) return;
    // if (!aptSuite || !areaInput) {
    //   alert("Please fill out Apt/Suite and Area fields");
    //   return;
    // }

    const combinedAddress = `${areaInput ? areaInput + ", " : ""}${tempLocation.address}`;

    // Create updated location with form data
    const updatedLocation: Location = {
      ...tempLocation,
      address: combinedAddress,
      FlatBlock: aptSuite ? aptSuite : tempLocation.address.split(",")[0], // Use aptSuite or fallback to first part of address
      deliveryInstructions: deliveryInstructions,
    };

    // Hide the area input form
    setShowAreaInput(false);

    // Set the temp location with updated data
    setTempLocation(updatedLocation);

    // Proceed with restaurant search - will save address only if deliverable
    await handleLocationSelect(updatedLocation);
    setDeliveryInstructions("");
  };

  const deliveryLocations: Location[] = addresses.map((addr) => ({
    id: addr._id || `addr-${Math.random()}`,
    name: addr.tag || addr.type,
    // Store the base address (Area) without FlatBlock for proper display construction
    address: addr.Area || "Address not specified",
    latitude: addr.latitude || 0,
    longitude: addr.longitude || 0,
    country: "US",
    state: "",
    city: "",
    FlatBlock: addr.FlatBlock,
    deliveryInstructions: addr.deliveryInstructions,
  }));

  useEffect(() => {
    if (!isOpen || orderType !== "pickup") return;

    const fetchPickupLocations = async () => {
      try {
        setLoadingLocations(true);

        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/admin/get-hotel-info`,
        );

        if (!res.data?.success) return;

        const formatted: Location[] = res.data.data.map((hotel: any) => ({
          id: hotel._id,
          hotelName: hotel.hotelName,
          name: hotel.hotelName,
          address: hotel.address,
          latitude: hotel.latitude,
          longitude: hotel.longitude,
          country: hotel.country,
          state: hotel.state,
          city: hotel.city,
          defaultTip: hotel.defaultTip,
          phoneNo: hotel.phoneNo,
          pickupTimeline: hotel.pickupTimeline,
          deliveryTimeline: hotel.deliveryTimeline,
          storeTimeline: hotel.storeTimeline,
          timezone: hotel.timezone,
          _timezoneInfo: hotel._timezoneInfo,
          _storeStatus: hotel._storeStatus,
          deliveryTipToPartner: hotel.deliveryTipToPartner,
          baseTipPercent: hotel.baseTipPercent,
          tipGapPercent: hotel.tipGapPercent,
          withinRadius: hotel.withinRadius,
          minOrderAmount: hotel.minOrderAmount,
          deliveryAmount: hotel.deliveryAmount,
          asapDelay: hotel.asapDelay,
          taxPercentage: hotel.taxes,
          cashEnabled: hotel.cashEnabled,
          integrations: hotel.integrations,
        }));

        setPickupLocations(formatted);

        // Auto-select if only one pickup location
        if (formatted.length === 1) {
          const singleLocation = formatted[0];
          handleLocationSelect(singleLocation);
          updateTaxesForRestaurant(
            singleLocation.id,
            singleLocation.taxPercentage || [],
          );

          // Also set the display values so it shows in the pickup location block
          setSelectedLocation(singleLocation.name);
          onLocationObjectSelect?.(singleLocation);
          setSelectedAddress(true);
        }
      } catch (err) {
        console.error("Failed to fetch pickup locations", err);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchPickupLocations();
  }, [isOpen, orderType]);

  const locations =
    orderType === "pickup" ? pickupLocations : deliveryLocations;
  const selectedLocationData = tempLocation
    ? locations.find((loc) => loc.id === tempLocation.id) || tempLocation
    : null;

  const applySquareCredentials = (integrations: Location["integrations"]) => {
    const square = integrations?.square;
    if (square?.configured && square?.credentials) {
      setSquareApplicationId(square.credentials.SQUARE_APPLICATION_ID || null);
      setSquareLocationId(square.credentials.SQUARE_LOCATION_ID || null);
    } else {
      setSquareApplicationId(null);
      setSquareLocationId(null);
    }
  };

  const handleLocationSelect = async (location: Location) => {
    setTempLocation(location);
    setSelectedRestaurant(null);
    setDeliveryError(false);
    if (
      orderType === "delivery" &&
      !location.isSearchedAddress &&
      location.id?.length > 10
    ) {
      setInitialAddressData({
        FlatBlock: location.FlatBlock || "",
        deliveryInstructions: location.deliveryInstructions || "",
      });
    }

    // Populate fields from saved address data
    if (location.FlatBlock) {
      setAptSuite(location.FlatBlock);
    }
    if (location.deliveryInstructions) {
      setDeliveryInstructions(location.deliveryInstructions);
    }
    // Sync address type tag when selecting a saved delivery address
    if (orderType === "delivery" && !location.isSearchedAddress) {
      const normalizedType =
        location.name === "Home" ||
        location.name === "Office" ||
        location.name === "Apartment" ||
        location.name === "Hotel"
          ? (location.name as typeof selectedAddressType)
          : "Others";
      setSelectedAddressType(normalizedType);
    }

    // Convert Location to LocationObject for context storage
    const locationObject = {
      id: location.id,
      hotelName: location.hotelName,
      name: location.name,
      address: location.address,
      latitude: location.latitude,
      longitude: location.longitude,
      country: location.country,
      state: location.state,
      city: location.city,
      phoneNo: location.phoneNo,
      pickupTimeline: location.pickupTimeline,
      deliveryTimeline: location.deliveryTimeline,
      storeTimeline: location.storeTimeline,
      defaultTip: location.defaultTip,
      distance: location.distance,
      timezone: location.timezone,
      _timezoneInfo: location._timezoneInfo,
      _storeStatus: location._storeStatus,
      isSearchedAddress: location.isSearchedAddress,
      FlatBlock: location.FlatBlock,
      deliveryInstructions: location.deliveryInstructions,
      deliveryTipToPartner: location.deliveryTipToPartner,
      baseTipPercent: location.baseTipPercent,
      tipGapPercent: location.tipGapPercent,
      withinRadius: location.withinRadius,
      minOrderAmount: location.minOrderAmount,
      deliveryAmount: location.deliveryAmount,
      asapDelay: location.asapDelay,
      taxPercentage: location.taxPercentage,
      cashEnabled: location.cashEnabled,
    };

    if (orderType === "delivery") {
      console.log("Selected delivery location:", location);

      const finalAddress = buildFullDeliveryAddress(location);

      // FIX: Don't call onLocationObjectSelect here for delivery
      // We'll call it after finding the restaurant in handleFindRestaurant

      setDelivery((prev) => ({
        ...prev,
        displayAddress: finalAddress,
        locationObject,
        addressTag: location.name,
        instructions: location.deliveryInstructions || prev.instructions,
      }));

      setDeliveryAddress(finalAddress);
      setSelectedAddressTag(location.name);
      console.log("Selected delivery address tag:", location.name, orderData);
      setOrderData((prev) => ({
        ...prev,
        location: {
          address: finalAddress,
          lat: location.latitude,
          lng: location.longitude,
        },
      }));

      await handleFindRestaurant(location);
    } else {
      // For pickup, location IS the restaurant, so pass it immediately
      setPickup((prev) => ({
        ...prev,
        displayAddress: location.address,
        locationObject,
      }));

      setPickupLocation(location.address);
      setOrderData((prev) => ({
        ...prev,
        restaurantId: location.id,
        pickupLocation: {
          hotelName: location.hotelName || location.name,
          address: location.address,
          phoneNo: location.phoneNo || "",
          latitude: location.latitude,
          longitude: location.longitude,
        },
      }));

      // Set tax percentage for pickup location (only updates if restaurant changed)
      updateTaxesForRestaurant(location.id, location.taxPercentage || []);

      // Update Square credentials for the selected pickup restaurant
      applySquareCredentials(location.integrations);

      // FIX: Call onLocationObjectSelect immediately for pickup
      onLocationObjectSelect?.(locationObject as Location);
      console.log(
        "Selected pickup location:",
        location.name,
        "restaurantId:",
        location.id,
      );
    }
  };

  const handleFindRestaurant = async (location: Location) => {
    try {
      setFetchingRestaurant(true);
      setDeliveryError(false);
      const fullDropoffAddress = buildFullDeliveryAddress(location);

      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/uber/uber-genrate-order-quote`,
        {
          dropoff_address: fullDropoffAddress,
          dropoff_latitude: location.latitude,
          dropoff_longitude: location.longitude,
        },
      );

      const data = res.data;

      if (data?.nearestRestaurant) {
        const nearestRestaurantId =
          data.nearestRestaurant.id ||
          data.nearestRestaurant._id ||
          "restaurant";

        let resolvedTaxes: Taxes[] = data.nearestRestaurant.taxes || [];
        let resolvedIntegrations: Location["integrations"] =
          data.nearestRestaurant.integrations;
        if (!resolvedTaxes.length || !resolvedIntegrations) {
          try {
            const hotelRes = await getHotelInformation();
            const hotels =
              hotelRes.data?.data ||
              hotelRes.data?.hotels ||
              hotelRes.data ||
              [];
            const match = Array.isArray(hotels)
              ? hotels.find(
                  (h: any) =>
                    h?._id === nearestRestaurantId || h?.id === nearestRestaurantId,
                )
              : null;
            if (!resolvedTaxes.length) {
              resolvedTaxes = match?.taxes || match?.taxPercentage || [];
            }
            if (!resolvedIntegrations) {
              resolvedIntegrations = match?.integrations;
            }
          } catch (err) {
            console.error("Failed to load restaurant data", err);
          }
        }

        // Update Square credentials for the nearest delivery restaurant
        applySquareCredentials(resolvedIntegrations);

        const restaurantData = {
          id: nearestRestaurantId,
          hotelName: data.nearestRestaurant.hotelName,
          name: data.nearestRestaurant.hotelName,
          address: data.nearestRestaurant.address,
          latitude: data.nearestRestaurant.latitude,
          longitude: data.nearestRestaurant.longitude,
          country: "US",
          state: "",
          city: "",
          phoneNo: data.nearestRestaurant.phoneNo,
          distance: data.nearestRestaurant.distance,
          deliveryTimeline: data.nearestRestaurant.deliveryTimeline,
          pickupTimeline: data.nearestRestaurant.pickupTimeline,
          storeTimeline: data.nearestRestaurant.storeTimeline,
          defaultTip: data.nearestRestaurant.defaultTip,
          timezone: data.nearestRestaurant.timezone,
          _timezoneInfo: data.nearestRestaurant._timezoneInfo,
          _storeStatus: data.nearestRestaurant._storeStatus,
          deliveryTipToPartner: data.nearestRestaurant.deliveryTipToPartner,
          baseTipPercent: data.nearestRestaurant.baseTipPercent,
          tipGapPercent: data.nearestRestaurant.tipGapPercent,
          withinRadius: data.nearestRestaurant.withinRadius,
          minOrderAmount: data.nearestRestaurant.minOrderAmount,
          deliveryAmount: data.nearestRestaurant.deliveryAmount,
          asapDelay: data.nearestRestaurant.asapDelay,
          taxPercentage: resolvedTaxes,
          cashEnabled: data.nearestRestaurant.cashEnabled,
        };

        setSelectedRestaurant(restaurantData);
        updateTaxesForRestaurant(nearestRestaurantId, resolvedTaxes);
        setOrderData((prev) =>
          prev
            ? {
                ...prev,
                restaurantId: nearestRestaurantId,
              }
            : prev,
        );

        // Self-delivery (flat delivery): a restaurant that has withinRadius +
        // deliveryAmount + minOrderAmount configured delivers the order itself
        // for a flat fee, as long as the address is within that radius and the
        // cart meets the minimum order amount — no separate backend flag exists.
        const hasFlatDeliveryPricing =
          typeof restaurantData.withinRadius === "number" &&
          restaurantData.withinRadius > 0 &&
          typeof restaurantData.deliveryAmount === "number" &&
          restaurantData.deliveryAmount > 0 &&
          typeof restaurantData.minOrderAmount === "number";

        const selfDeliveryDistanceMiles =
          parseDistanceMiles(restaurantData.distance) ??
          (typeof location.latitude === "number" &&
          typeof location.longitude === "number" &&
          typeof restaurantData.latitude === "number" &&
          typeof restaurantData.longitude === "number"
            ? calculateStraightLineMiles(
                location.latitude,
                location.longitude,
                restaurantData.latitude,
                restaurantData.longitude,
              )
            : null);

        const isWithinSelfDeliveryRadius =
          hasFlatDeliveryPricing &&
          selfDeliveryDistanceMiles !== null &&
          selfDeliveryDistanceMiles <= (restaurantData.withinRadius || 0);

        const meetsSelfDeliveryMinOrder =
          cartSubtotal >= (restaurantData.minOrderAmount || 0);

        const isSelfDeliveryEligible =
          isWithinSelfDeliveryRadius && meetsSelfDeliveryMinOrder;

        const isAddressDeliverable =
          Boolean(res.data.quotes?.uber?.success) ||
          Boolean(res.data.quotes?.doordash?.success) ||
          isSelfDeliveryEligible;

        if (isAddressDeliverable) {
          // Only update delivery context when actually deliverable — prevents
          // the useEffect from re-firing and resetting deliveryError
          onLocationObjectSelect?.(restaurantData);
          setDelivery((prev) => ({
            ...prev,
            restaurantLocation: restaurantData,
          }));
          setDeliverable(true);
          setDeliveryError(false);
          setPickupOnlyNotice(false);
        } else {
          setDeliveryError(true);
          setPickupOnlyNotice(true);
        }

        // Save address only if it's deliverable AND it's a searched address AND user is logged in
        if (location.isSearchedAddress && user?.id && isAddressDeliverable) {
          try {
            setSavingAddress(true);
            const normalizedType: "Home" | "Office" | "Others" =
              selectedAddressType === "Home" || selectedAddressType === "Office"
                ? selectedAddressType
                : "Others";

            const newAddress = {
              userId: user?.id,
              type: normalizedType,
              tag: selectedAddressType,
              FlatBlock: aptSuite || "",
              Area: areaInput
                ? `${areaInput}, ${location.address}`
                : location.address,
              deliveryInstructions: deliveryInstructions || "",
              latitude: location.latitude,
              longitude: location.longitude,
            };

            const response = await api.post(
              `${process.env.REACT_APP_API_URL}/address`,
              newAddress,
            );

            setAddresses((prev) => [...prev, response.data.address]);

            setTempLocation({
              ...location,
              id: response.data.address._id || location.id,
              isSearchedAddress: false,
            });
          } catch (error) {
            console.error("Error adding address:", error);
            toast.error("Failed to save address");
          } finally {
            setSavingAddress(false);
          }
        }
      } else {
        setDeliveryError(true);
        setSelectedRestaurant(null);
        // FIX: Clear the location object when no restaurant found
        onLocationObjectSelect?.(null);
      }
    } catch (err) {
      console.error(err);
      setDeliveryError(true);
      setSelectedRestaurant(null);
      // FIX: Clear the location object on error
      onLocationObjectSelect?.(null);
    } finally {
      setFetchingRestaurant(false);
    }
  };

  const handleScheduleOrder = async () => {
    if (
      orderType === "delivery" &&
      tempLocation &&
      !tempLocation.isSearchedAddress &&
      tempLocation.id?.length > 10 &&
      initialAddressData
    ) {
      const updates: {
        FlatBlock?: string;
        deliveryInstructions?: string;
      } = {};

      if (aptSuite !== initialAddressData.FlatBlock) {
        updates.FlatBlock = aptSuite;
      }

      if (deliveryInstructions !== initialAddressData.deliveryInstructions) {
        updates.deliveryInstructions = deliveryInstructions;
      }

      // 🔥 Only call API if something actually changed
      if (Object.keys(updates).length > 0) {
        await updateAddressInBackend(tempLocation.id, updates);
      }
    }

    if (orderType === "delivery") {
      if (selectedRestaurant && tempLocation) {
        const finalAddress = aptSuite.trim()
          ? `${aptSuite.trim()}, ${tempLocation.address}`
          : tempLocation.address;

        setSelectedLocation(finalAddress);
        setDeliveryAddress(finalAddress);
        onLocationObjectSelect?.(selectedRestaurant);
        setSelectedAddress(true);

        setOrderData((prev) => ({
          ...prev,
          restaurantId: selectedRestaurant.id,
          location: {
            address: finalAddress,
            lat: tempLocation.latitude,
            lng: tempLocation.longitude,
          },
        }));
      }
    } else {
      if (tempLocation) {
        setSelectedLocation(tempLocation.name);
        onLocationObjectSelect?.(tempLocation);
        setSelectedAddress(true);
      }
    }

    onLocationConfirmed?.();
    onClose();
    onScheduleClick();
  };

  // Handle ASAP confirmation for pickup orders
  const handleConfirmAsap = () => {
    if (!tempLocation) return;

    // Set the pickup location
    setSelectedLocation(tempLocation.name);
    onLocationObjectSelect?.(tempLocation);
    setSelectedAddress(true);

    // Get timezone for ASAP calculation
    const timezone =
      tempLocation.timezone ||
      tempLocation._timezoneInfo?.id ||
      "America/Denver";

    // Set order data with ASAP flag and calculated scheduled time
    const asapTime = getAsapScheduledTime(timezone, tempLocation.asapDelay);
    setOrderData((prev) => ({
      ...prev,
      restaurantId: tempLocation.id,
      pickupLocation: {
        hotelName: tempLocation.hotelName || tempLocation.name,
        address: tempLocation.address,
        phoneNo: tempLocation.phoneNo || "",
        latitude: tempLocation.latitude,
        longitude: tempLocation.longitude,
      },
      isAsap: true,
      scheduledAt: asapTime.isoString,
      scheduledTimezone: timezone,
    }));

    // Close modal without opening time picker (ASAP doesn't need scheduling)
    onLocationConfirmed?.();
    onClose();
  };

  const handleChangeLocation = () => {
    setTempLocation(null);
    setSelectedRestaurant(null);
    setSelectedAddress(false);
    setAptSuite("");
    setAreaInput("");
    setShowAreaInput(false);
    setDeliveryError(false);
    setPickupOnlyNotice(false);
    setDeliveryInstructions("");
    setSelectedAddressType("Home");
    setInitialAddressData(null);
    if (orderType === "delivery") {
      setDeliveryAddress("");
      setSelectedAddressTag("");
      setDelivery((prev) => ({
        ...prev,
        displayAddress: "",
        locationObject: null,
        restaurantLocation: null,
      }));
      setOrderData((prev) =>
        prev
          ? {
              ...prev,
              location: undefined,
            }
          : prev,
      );
    }
    onLocationObjectSelect?.(null);
  };

  // Function to update address via PUT API
  const updateAddressInBackend = async (
    addressId: string,
    updates: { FlatBlock?: string; deliveryInstructions?: string },
  ) => {
    if (!addressId || !user?.id) return;

    try {
      setUpdatingAddress(true);
      await api.put(
        `${process.env.REACT_APP_API_URL}/address/${addressId}`,
        updates,
      );
      console.log("✅ Address updated successfully");

      // Update the addresses state with new values
      setAddresses((prev) =>
        prev.map((addr) =>
          addr._id === addressId ? { ...addr, ...updates } : addr,
        ),
      );
    } catch (error) {
      console.error("❌ Failed to update address:", error);
    } finally {
      setUpdatingAddress(false);
    }
  };

  // Handler for aptSuite change with API update
  const handleAptSuiteChange = (value: string) => {
    setAptSuite(value);

    if (tempLocation) {
      const finalAddress = value.trim()
        ? `${value.trim()}, ${tempLocation.address}`
        : tempLocation.address;

      // Update context
      setDeliveryAddress(finalAddress);
      setDelivery((prev) => ({
        ...prev,
        displayAddress: finalAddress,
      }));
      // setOrderData({
      //   ...orderData,
      //   location: {
      //     address: finalAddress,
      //     lat: tempLocation.latitude,
      //     lng: tempLocation.longitude,
      //   },
      // });
    }
  };

  // Handler for delivery instructions change with API update
  const handleDeliveryInstructionsChange = (value: string) => {
    setDeliveryInstructions(value);

    // Update context
    setDelivery((prev) => ({
      ...prev,
      instructions: value,
    }));

    // Update backend if this is a saved address
    if (
      tempLocation &&
      tempLocation.id &&
      !tempLocation.isSearchedAddress &&
      tempLocation.id.length > 10
    ) {
      // Debounce the API call
      const timeoutId = setTimeout(() => {
        updateAddressInBackend(tempLocation.id, {
          deliveryInstructions: value,
        });
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  };

  if (!isOpen) return null;

  const openingStatus = selectedLocationData
    ? getTodayOpeningText(selectedLocationData, orderType)
    : "Closed";

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999] m-0"
      onClick={onClose}
    >
      <div
        className="dm-order-modal bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-black/10"
        onClick={(e) => e.stopPropagation()}
      >
        <style>
          {`
            div::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>

        {/* Header */}
        <div className="shrink-0 bg-white px-6 py-3 flex items-center justify-between border-b border-black/5">
          <h2 className="text-xl font-semibold text-black">Order details</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close order details"
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-6 space-y-6 no-scrollbar">
          {/* Order Type Toggle */}
          <div className="relative flex bg-white rounded-full p-1 w-full border border-black/20 dm-toggle">
        <span
          className={`absolute top-1 bottom-1 rounded-full bg-black dm-toggle-indicator 
        transition-all duration-300 ease-out
        ${orderType === "delivery" ? "left-1/2 right-1" : "left-1 right-1/2"}`}
        />

            <button
              onClick={() => {
                setOrderType("pickup");
              }}
              className={`relative z-10 flex-1 px-3 py-2 text-sm font-medium rounded-full
                transition-colors duration-200
                ${orderType === "pickup" ? "text-white dm-toggle-selected" : "text-black dm-toggle-unselected"} dm-toggle-btn`}
            >
              Pickup
            </button>

            <button
              onClick={() => {
                setOrderType("delivery");
              }}
              className={`relative z-10 flex-1 px-3 py-2 text-sm font-medium rounded-full
                transition-colors duration-200
                ${orderType === "delivery" ? "text-white dm-toggle-selected" : "text-black dm-toggle-unselected"} dm-toggle-btn`}
            >
              Delivery
            </button>
          </div>

          {/* Show area input form for searched addresses */}
          {showAreaInput && tempLocation?.isSearchedAddress ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-black">
                Complete your address
              </h3>

              <div className="flex items-start gap-2.5 text-gray-700 bg-gray-50 p-3 rounded-xl">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-500" />
                <span className="text-sm leading-relaxed">
                  {tempLocation.address}
                </span>
              </div>

              <input
                value={aptSuite}
                onChange={(e) => setAptSuite(e.target.value)}
                placeholder="Apt / Suite / Floor"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-300"
              />

              <input
                value={areaInput}
                onChange={(e) => setAreaInput(e.target.value)}
                placeholder="Area / Landmark"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-300"
              />

              <textarea
                value={deliveryInstructions}
                onChange={(e) => setDeliveryInstructions(e.target.value)}
                placeholder="Enter any specific delivery instructions (optional)"
                maxLength={280}
                className="w-full h-20 overflow-y-auto resize-none px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-300"
              />

             {/* Address Type Selection */}
                <div className="relative">
                  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scroll-smooth no-scrollbar">
                    {(
                      ["Home", "Office", "Apartment", "Hotel", "Others"] as const
                    ).map((type) => (
                      <button
                        key={type}
                        onClick={() => setSelectedAddressType(type)}
                        className={`px-4 py-2 rounded-xl font-medium transition-colors flex-shrink-0 ${
                          selectedAddressType === type
                            ? "dm-address-tag-selected"
                            : "dm-address-tag-unselected"
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAreaInput(false);
                    setTempLocation(null);
                    setAptSuite("");
                    setAreaInput("");
                    setDeliveryInstructions("");
                  }}
                  className="flex-1 py-3 border-2 border-gray-300 rounded-2xl font-semibold text-black hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSearchedAddress}
                  disabled={savingAddress}
                  className={`flex-1 py-3 rounded-2xl font-semibold transition-all ${
                    savingAddress
                      ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                      : "bg-black text-white dm-address-cta hover:bg-gray-800"
                  }`}
                >
                  {savingAddress ? (
                    <span className="flex items-center justify-center gap-2">
                      <VideoLoader size="small" />
                      Saving...
                    </span>
                  ) : (
                    "Continue"
                  )}
                </button>
              </div>
            </div>
          ) : tempLocation && selectedLocationData ? (
            // Selected Location View
            <div className="space-y-6">
              {/* Delivery Address */}
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-3 flex-1">
                  <h3 className="text-xl font-semibold text-black leading-tight">
                    {orderType === "delivery"
                      ? "Delivery Address"
                      : selectedLocationData.name
                          .replace(/Fisherman's Wharf Hotel|Hotel/gi, "")
                          .trim() || selectedLocationData.name}
                  </h3>
                  <div>
                    <div className="flex items-start gap-2.5 text-gray-700">
                      <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-500" />
                      <span className="text-sm sm:text-[15px] leading-relaxed">
                        {selectedLocationData.address}
                      </span>
                    </div>
                    {orderType === "pickup" && (
                      <div className="flex items-center gap-2.5 text-gray-700">
                        {openingStatus === "Open" ? (
                          <Sun className="w-5 h-5 flex-shrink-0 text-gray-500" />
                        ) : (
                          <Moon className="w-5 h-5 flex-shrink-0 text-gray-500" />
                        )}
                        <span className="text-[15px]">{openingStatus}</span>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleChangeLocation();
                    setSelectedLocation(null);
                  }}
                  className="dm-white-btn dm-no-hover px-3 sm:px-5 py-2 sm:py-2.5 border-2 border-gray-300 rounded-xl font-medium text-black hover:bg-gray-50 transition-colors text-xs sm:text-sm flex-shrink-0"
                >
                  Change
                </button>
              </div>

              {orderType === "delivery" && !tempLocation.isSearchedAddress && (
                <div className="flex flex-col gap-2">
                  <input
                    value={aptSuite}
                    onChange={(e) => handleAptSuiteChange(e.target.value)}
                    placeholder="Apt / Suite / Floor"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-300"
                  />
                  <textarea
                    value={deliveryInstructions}
                    onChange={(e) => setDeliveryInstructions(e.target.value)}
                    placeholder="Enter any specific delivery instructions"
                    maxLength={280}
                    className="w-full h-18 overflow-y-auto resize-none px-4 py-2 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-300"
                  />
                  {updatingAddress && (
                    <span className="text-xs text-gray-500">Saving...</span>
                  )}
                </div>
              )}

              {/* Delivery Error */}
              {orderType === "delivery" && deliveryError && (
                <div className="space-y mt-1">
                  <p className="text-red-600 font-semibold text-lg mb-1">
                    {pickupOnlyNotice
                      ? "This address is available for pickup only"
                      : "No location deliver to this address"}
                  </p>
                  {selectedRestaurant && (
                    <p className="text-gray-600 text-sm mb-2">
                      {pickupOnlyNotice
                        ? `${selectedRestaurant.name} does not offer delivery. You can place a pickup order from ${selectedRestaurant.name} or try a different address.`
                        : `Unfortunately, your nearest location ${selectedRestaurant.name} is not delivering. But you can pickup from ${selectedRestaurant.name} or try changing the address to find a deliverable restaurant nearby.`}
                    </p>
                  )}

                  <div className="flex items-center gap-1">
                    <button
                      onClick={handleChangeLocation}
                      className="text-black font-medium underline"
                    >
                      Change address
                    </button>
                    <p className="text-gray-600 text-sm mb-0">
                      to find another location.
                    </p>
                  </div>
                </div>
              )}

              {/* Restaurant Card - Show when restaurant exists (with or without delivery error) */}
              {orderType === "delivery" && selectedRestaurant && (
                <div className="pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-semibold text-black mb-4">
                    {deliverable === false
                      ? "Nearest location for pickup"
                      : "Delivering from"}
                  </h4>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                      <span className="toggle-selected-text text-lg font-semibold">
                        {selectedRestaurant.name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <h3 className="text-base sm:text-lg font-semibold text-black">
                          {selectedRestaurant.name
                            .replace(/Fisherman's Wharf Hotel|Hotel/gi, "")
                            .trim() || selectedRestaurant.name}
                        </h3>
                        {selectedRestaurant.distance && (
                          <span className="text-sm text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full flex-shrink-0">
                            {selectedRestaurant.distance}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        {getTodayOpeningText(selectedRestaurant, "delivery") ===
                        "Open" ? (
                          <Sun className="w-4 h-4 flex-shrink-0" />
                        ) : (
                          <Moon className="w-4 h-4 flex-shrink-0" />
                        )}
                        <span className="text-sm">
                          {getTodayOpeningText(selectedRestaurant, "delivery")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Location List View
            <>
              {/* Search Input */}
              {orderType === "delivery" && (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-4 top-5 w-4 h-4 text-gray-500" />

                    <input
                      value={searchText}
                      onChange={(e) => handleSearchTextChange(e.target.value)}
                      onFocus={handleSearchFocus}
                      onBlur={handleSearchBlur}
                      placeholder="Enter delivery address..."
                      className="w-full pl-12 text-sm pr-4 py-3 border-2 border-black/5 text-black placeholder-gray-500 rounded-2xl focus:outline-none"
                    />

                    {searching && (
                      <div className="absolute right-4 top-4 text-xs text-gray-400">
                        searching…
                      </div>
                    )}

                    {geoResults.length > 0 && (
                      <div className="absolute z-50 mt-2 w-full bg-white rounded-2xl border border-gray-200 max-h-72 overflow-y-auto">
                        {geoResults.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => {
                              void handleGeoSelect(item);
                              setDeliveryInstructions("");
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-100 flex items-start gap-3"
                          >
                            <MapPin className="w-5 h-5 mt-1 text-gray-500" />
                            <span className="text-sm text-gray-800">
                              {item.address}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Locations List */}
              <div className="space-y-0 divide-y divide-border/5">
                {loadingLocations ? (
                  <div className="flex justify-center items-center py-12">
                    <VideoLoader size="small" />
                  </div>
                ) : locations.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No locations available
                  </div>
                ) : (
                  locations.map((location) => {
                    const locationStatus = getTodayOpeningText(
                      location,
                      orderType,
                    );
                    return (
                      <div
                        key={location.id}
                        className="flex items-center justify-between py-3 first:pt-2"
                      >
                        <div className="flex-1 pr-4">
                          <h3 className="text-base sm:text-[19px] font-semibold text-black leading-tight">
                            {location.name
                              .replace(/Fisherman's Wharf Hotel|Hotel/gi, "")
                              .trim() || location.name}
                          </h3>
                          <div className="flex items-start gap-2 text-gray-600">
                            <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-gray-500" />
                            <span className="text-sm sm:text-[15px] leading-relaxed">
                              {location.address}
                            </span>
                          </div>
                          {orderType === "pickup" && (
                            <div className="flex items-center gap-2 text-gray-600">
                              {locationStatus === "Open" ? (
                                <Sun className="w-4 h-4 flex-shrink-0 text-gray-500" />
                              ) : (
                                <Moon className="w-4 h-4 flex-shrink-0 text-gray-500" />
                              )}
                              <span className="text-[15px]">
                                {locationStatus}
                              </span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleLocationSelect(location)}
                          className="dm-white-btn dm-no-hover px-3 sm:px-5 py-2 sm:py-2.5 border-2 border-black/5 rounded-xl font-medium text-black hover:bg-gray-50 transition-colors text-xs sm:text-sm flex-shrink-0 mt-1"
                        >
                          Order
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer Button - Only show when location is selected and not in area input mode */}
        {tempLocation && !deliveryError && !showAreaInput && (
          <div className="shrink-0 bg-white p-6 border-t border-gray-100 flex flex-col gap-3">
            {/* Confirm ASAP button - Only show for pickup when restaurant is open */}
            {isAsapAvailable && (
              <button
                onClick={handleConfirmAsap}
                disabled={fetchingRestaurant}
                className={`dm-white-btn w-full py-3 sm:py-4 text-lg font-semibold rounded-2xl transition-all
                  ${
                    fetchingRestaurant
                      ? "bg-transparent text-gray-400 cursor-not-allowed"
                      : "bg-black toggle-selected-text hover:bg-gray-800"
                  }`}
              >
                Confirm ASAP
              </button>
            )}
            <button
              onClick={handleScheduleOrder}
              disabled={fetchingRestaurant}
              className={`dm-white-btn dm-white-btn-static dm-no-hover w-full py-3 sm:py-4 text-lg font-semibold rounded-2xl transition-all
                ${
                  fetchingRestaurant
                    ? "bg-transparent text-gray-400 cursor-not-allowed"
                    : isAsapAvailable
                      ? "bg-white text-black border-2 border-black/5 hover:bg-gray-50"
                      : "bg-black toggle-selected-text hover:bg-gray-800"
                }`}
            >
              {fetchingRestaurant ? (
                <span className="flex items-center justify-center gap-2">
                  <VideoLoader size="small" />
                  Finding restaurant…
                </span>
              ) : orderType === "pickup" ? (
                "Schedule pickup"
              ) : (
                "Schedule delivery"
              )}
            </button>
          </div>
        )}

        {/* Footer when delivery error */}
        {tempLocation && deliveryError && orderType === "delivery" && (
          <div className="sticky bottom-0 bg-white p-6 border-t border-black/5">
            <button
              disabled
              className="w-full py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-2xl bg-gray-200 text-gray-600 cursor-not-allowed"
            >
              Schedule delivery
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export interface TimePickerModalProps {
  isOpen: boolean;
  onClose: () => void;

  selectedDate: string;
  setSelectedDate: (date: string) => void;

  selectedTime: string;
  setSelectedTime: (time: string) => void;

  selectedLocation?: Location | null;
  orderType?: "pickup" | "delivery";
  setSelectedTimemodal: React.Dispatch<React.SetStateAction<boolean>>;
}

export function TimePickerModal({
  isOpen,
  onClose,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  selectedLocation,
  setSelectedTimemodal,
  orderType = "pickup",
}: TimePickerModalProps) {
  const [showMoreDates, setShowMoreDates] = useState(false);
  const [tempDate, setTempDate] = useState<string>("today");
  const [tempTime, setTempTime] = useState<string>("");
  const [isAsapSelected, setIsAsapSelected] = useState(false);

  // Get order context
  const { orderData, setOrderData } = useOrder();

  // Check if restaurant is currently open
  const isCurrentlyOpen = isRestaurantCurrentlyOpen(
    selectedLocation,
    orderType,
  );

  // Get the hotel's timezone, fallback to America/Denver (MST) if not provided
  const hotelTimezone =
    selectedLocation?.timezone ||
    selectedLocation?._timezoneInfo?.id ||
    "America/Denver";

  // Helper function to generate dates based on hotel's timezone
  function generateDates(count = 5) {
    const result = [];

    for (let i = 0; i < count; i++) {
      const dateInfo = getDateInTimezone(hotelTimezone, i);

      const id = i === 0 ? "today" : i === 1 ? "tomorrow" : dateInfo.dateString;

      result.push({
        id,
        label: dateInfo.label,
        date: dateInfo.formattedDate,
        dayOfWeek: dateInfo.dayOfWeek,
      });
    }

    return result;
  }

  const dates = generateDates(5);

  // Check if a date is closed
  const isDateClosed = (dateId: string): boolean => {
    if (!selectedLocation) return false;

    const selectedDateObj = dates.find((d) => d.id === dateId);
    if (!selectedDateObj) return false;

    const dayOfWeek = selectedDateObj.dayOfWeek;
    const timeline =
      orderType === "pickup"
        ? selectedLocation.pickupTimeline
        : selectedLocation.deliveryTimeline;

    if (!timeline) return false;

    const daySchedule = timeline.find((t) => t.day === dayOfWeek);
    return (
      !daySchedule || !daySchedule.isOpen || daySchedule.slots.length === 0
    );
  };

  // Generate time slots from timeline using hotel's timezone
  function generateTimeSlotsFromTimeline(dateId: string): string[] {
    if (!selectedLocation) {
      return generateDefaultTimeSlots();
    }

    const selectedDateObj = dates.find((d) => d.id === dateId);
    if (!selectedDateObj) return [];

    const dayOfWeek = selectedDateObj.dayOfWeek;
    const timeline =
      orderType === "pickup"
        ? selectedLocation.pickupTimeline
        : selectedLocation.deliveryTimeline;

    if (!timeline) return generateDefaultTimeSlots();

    const isToday = dateId === "today";

    // Use the centralized timezone-aware slot generation
    return generateTimeSlotsForDay(
      timeline,
      dayOfWeek,
      hotelTimezone,
      isToday,
      15, // 15-minute intervals
    );
  }

  // Generate default time slots (fallback when no location selected)
  function generateDefaultTimeSlots(
    startHour = 11,
    endHour = 22,
    interval = 15,
  ): string[] {
    const slots: string[] = [];
    const tzAbbr = getTimezoneAbbreviation(hotelTimezone);

    let currentMinutes = startHour * 60;
    const endMinutes = endHour * 60 + 30;

    while (currentMinutes <= endMinutes) {
      const hours = Math.floor(currentMinutes / 60);
      const mins = currentMinutes % 60;
      const period = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      slots.push(
        `${displayHours}:${mins.toString().padStart(2, "0")} ${period} ${tzAbbr}`,
      );
      currentMinutes += interval;
    }

    return slots;
  }

  // Initialize temp values when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempDate(selectedDate || "today");
      setTempTime(selectedTime);

      // Auto-select ASAP if restaurant is currently open
      if (isCurrentlyOpen) {
        setIsAsapSelected(true);
        setTempTime("");
      } else {
        setIsAsapSelected(false);
      }
    }
  }, [isOpen, selectedDate, selectedTime, isCurrentlyOpen]);

  // Auto-select first available date when modal opens
  useEffect(() => {
    if (isOpen && selectedLocation) {
      // Get all available dates with time slots
      const availableDates = dates.filter((date) => {
        if (isDateClosed(date.id)) return false;
        const slots = generateTimeSlotsFromTimeline(date.id);
        return slots.length > 0;
      });

      if (availableDates.length > 0) {
        // Only auto-select if current tempDate is not in available dates
        const isTempDateAvailable = availableDates.some(
          (d) => d.id === tempDate,
        );

        // If current date is not available, switch to first available date
        if (!isTempDateAvailable) {
          setTempDate(availableDates[0].id);
          setTempTime("");
          setIsAsapSelected(false); // Can't use ASAP if today is not available
        }
      }
    }
  }, [isOpen, selectedLocation]);

  // Convert date string and time string to ISO string using hotel's timezone
  const convertToScheduledDateISO_local = (
    dateId: string,
    timeStr: string,
  ): string => {
    return convertToScheduledDateISO(dateId, timeStr, hotelTimezone);
  };

  const handleSchedule = () => {
    // Handle ASAP selection
    // Don't set scheduledAt here - it will be calculated at checkout time
    if (isAsapSelected) {
      setSelectedDate("today");
      setSelectedTime("ASAP");

      setOrderData((prev) => ({
        ...prev,
        scheduledAt: null as any, // Will be set at checkout
        scheduledTimezone: hotelTimezone,
        isAsap: true,
      }));

      console.log(
        "ASAP order selected - scheduledAt will be calculated at checkout",
        "Timezone:",
        hotelTimezone,
      );
      setSelectedTimemodal(true);
      onClose();
      return;
    }

    setSelectedDate(tempDate);
    setSelectedTime(tempTime);

    // Update order context with scheduledAt date in ISO format using hotel's timezone
    if (tempTime) {
      const isoDateString = convertToScheduledDateISO_local(tempDate, tempTime);

      setOrderData((prev) => ({
        ...prev,
        scheduledAt: isoDateString as any,
        // Store the timezone for proper display later
        scheduledTimezone: hotelTimezone,
        isAsap: false,
      }));

      console.log(
        "Scheduled date set (ISO):",
        isoDateString,
        "Timezone:",
        hotelTimezone,
      );
      setSelectedTimemodal(true);
    }

    onClose();
  };

  // Get available dates with time slots
  const getAvailableDates = () => {
    return dates.filter((date) => {
      if (isDateClosed(date.id)) return false;
      const slots = generateTimeSlotsFromTimeline(date.id);
      return slots.length > 0;
    });
  };

  const availableDates = getAvailableDates();
  const timeSlots = generateTimeSlotsFromTimeline(tempDate);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999] m-0">
      <div className="dm-order-modal bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-black/10">
        <div className="shrink-0 bg-white px-6 py-3 flex items-center justify-between border-b border-gray-100">
          <h2 className="text-xl font-semibold text-black">Order time</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close order time"
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain p-6 space-y-6 no-scrollbar">
          {/* Date Selection */}
          <div className="grid grid-cols-2 gap-3">
            {availableDates
              .slice(0, showMoreDates ? availableDates.length : 2)
              .map((date) => {
                const isSelected = tempDate === date.id;

                return (
                  <button
                    key={date.id}
                    onClick={() => {
                      setTempDate(date.id);
                      setTempTime("");
                      setIsAsapSelected(false);
                    }}
                    className={`dm-date-btn px-3 sm:px-5 py-2.5 rounded-2xl text-left transition-all duration-400 border-2
                      ${
                        isSelected
                          ? "dm-white-btn bg-black toggle-selected-text border-black"
                          : "bg-white border-gray-200 text-black hover:border-gray-300"
                      }`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col">
                        <span className="font-medium text-sm sm:text-base transition-colors duration-400">
                          {date.label}
                        </span>
                      </div>
                      <span
                        className={`dm-time-date text-xs sm:text-sm transition-colors duration-300 ${
                          isSelected ? "text-white dm-time-date-selected" : "text-gray-700 dm-time-date-unselected"
                        }`}
                      >
                        {date.date}
                      </span>
                    </div>
                  </button>
                );
              })}
          </div>
          <div className="w-full flex justify-center">
            {/* More Dates Button */}
            {availableDates.length > 2 && (
              <button
                onClick={() => setShowMoreDates(!showMoreDates)}
                className="dm-more-dates w-[50%] px-3 sm:px-5 py-2.5 rounded-2xl border-2 border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                {showMoreDates ? "Less dates" : "More dates"}
                <span
                  className={`transition-transform ${showMoreDates ? "rotate-180" : ""}`}
                >
                  <ChevronDown className="w-5 h-5" />
                </span>
              </button>
            )}
          </div>

          {/* Time Slots */}
          <div className="space-y-0">
            {/* ASAP Option - Only show when restaurant is currently open and date is today */}
            {isCurrentlyOpen && tempDate === "today" && (
              <button
                onClick={() => {
                  setIsAsapSelected(true);
                  setTempTime("");
                }}
                className="w-full py-3 border-b border-gray-200 flex items-center gap-4 hover:bg-gray-50 transition-colors"
              >
                <div
                  className={`dm-time-radio w-6 h-6 rounded-full border-2 flex items-center justify-center
                  ${isAsapSelected ? "border-black dm-time-radio-selected" : "border-gray-300 dm-time-radio-unselected"}`}
                >
                  {isAsapSelected && (
                    <div className="dm-time-radio-dot w-3 h-3 rounded-full bg-black" />
                  )}
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-md text-black font-semibold">ASAP</span>
                  <span className="text-xs text-gray-500">
                    Order will be placed immediately
                  </span>
                </div>
              </button>
            )}

            {timeSlots.map((time, index) => (
              <button
                key={index}
                onClick={() => {
                  setTempTime(time);
                  setIsAsapSelected(false);
                }}
                className="w-full py-3 border-b border-gray-200 flex items-center gap-4 hover:bg-gray-50 transition-colors"
              >
                <div
                  className={`dm-time-radio w-6 h-6 rounded-full border-2 flex items-center justify-center
                  ${tempTime === time && !isAsapSelected ? "border-black dm-time-radio-selected" : "border-gray-300 dm-time-radio-unselected"}`}
                >
                  {tempTime === time && !isAsapSelected && (
                    <div className="dm-time-radio-dot w-3 h-3 rounded-full bg-black" />
                  )}
                </div>
                <span className="text-md text-black">{time}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Schedule Button */}
        <div className="shrink-0 bg-white p-6 border-t border-gray-100">
          <button
            onClick={handleSchedule}
            disabled={!tempTime && !isAsapSelected}
            className={`dm-white-btn dm-no-hover w-full py-3 toggle-selected-text text-lg font-semibold rounded-2xl transition-colors
              ${
                tempTime || isAsapSelected
                  ? "bg-black hover:bg-gray-800"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
          >
            {isAsapSelected ? "Order now" : "Schedule order"}
          </button>
        </div>
      </div>
    </div>
  );
}
